<template>
  <button
      :class="btnClasses"
      :disabled="isLoading"
      @click="handleClick"
  >
    <template v-if="isLoading">
      <!-- Simplified spinner without the extra border effects -->
      <svg class="spinner h-5 w-5 inline-block mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span>{{ loadingText }}</span>
    </template>
    <template v-else>
      {{ buttonLabel }}
    </template>
  </button>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';

// Props definieren
const props = defineProps({
  variant: {
    type: String,
    default: 'default',
    validator: (v) => ['save', 'delete', 'default'].includes(v)
  },
  saveSuccessful: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ''
  },
  loadingText: {
    type: String,
    default: 'Speichern...'
  }
});

// Event-Emitter für Klicks
const emit = defineEmits(['click']);

// Isloading basierend auf dem saveSuccessful prop - WICHTIG: invertierte Logik!
// Wenn saveSuccessful = false: Button lädt
// Wenn saveSuccessful = true: Button hat fertig geladen
const isLoading = computed(() => !props.saveSuccessful);

// Computed: eigentlicher Button-Text
const buttonLabel = computed(() => {
  if (props.label) return props.label;
  if (props.variant === 'delete') return 'Löschen';
  if (props.variant === 'save') return 'Speichern';
  return 'Button';
});

// Klassen-Logik: Variante & Zustand
const btnClasses = computed(() => {
  let base = 'px-4 py-2 font-semibold rounded flex items-center justify-center transition-all duration-300 ';

  // Grundfarben nach Variante
  if (props.variant === 'save') base += 'bg-trader-green ';
  else if (props.variant === 'delete') base += 'bg-trader-red ';
  else base += 'bg-trader-gray ';

  if (isLoading.value) {
    // Vereinfachter Loading-Zustand ohne gestrichelten Rand
    base += 'opacity-60 cursor-not-allowed select-none ';
  } else {
    base += 'hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg ';
  }
  return base;
});

// Klick-Handler
function handleClick() {
  if (isLoading.value) return;
  emit('click');
}
</script>

<style scoped>
/* Nur die einfache Spinner-Animation beibehalten */
.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>